import { getAuth } from 'firebase/auth'
import { DocumentReference, DocumentSnapshot, getDoc, getDocFromCache, getDocs, getDocsFromCache } from 'firebase/firestore'
import { productsCollection, projectReference, projectsCollection } from './firebase'
import * as dt from 'modules/database/types'
import { NOT_LOGGED_IN_ERROR_MESSAGE } from 'modules/root/errors'

/** Check whether any locally cached documents have writes that haven't been uploaded. */
export async function anythingRequiresUpload(): Promise<boolean> {
	const user = getAuth().currentUser
	if (!user) {
		return false
	}

	const docs = await getDocsFromCache(projectsCollection(user))
	for (const doc of docs.docs) {
		if (doc.metadata.hasPendingWrites) {
			return true
		}

		const productDocs = await getDocsFromCache(productsCollection(user, doc.id))
		for (const productDoc of productDocs.docs) {
			if (productDoc.metadata.hasPendingWrites) {
				return true
			}
		}
	}

	return false
}

/**
 * Read a document from the local cache, returning `undefined` if it isn't cached.
 */
async function documentFromCacheOrUndefined<T>(ref: DocumentReference<T>): Promise<DocumentSnapshot<T> | undefined> {
	try {
		const doc = await getDocFromCache(ref)
		return doc.exists() ? doc : undefined
	} catch (error) {
		/* getDocFromCache rejects when the document isn't in the cache */
		return undefined
	}
}

/**
 * Read a whole project, preferring the local cache.
 *
 * The sync keeps the cache up to date and merges later server changes into the open project (see the
 * downloadedProject / downloadedProduct actions), so a server read here buys us nothing but latency —
 * and it's latency the user waits on, as opening a project blocks on this. Server reads of the products
 * collection have been seen to take 30s even while the connection is otherwise healthy.
 *
 * We only go to the server for a project that isn't cached at all, which is the one case where the cache
 * can't answer.
 */
export async function projectByIdOrUndefined(projectId: dt.ProjectID): Promise<dt.ProjectWithProducts | undefined> {
	const user = getAuth().currentUser
	if (!user) {
		throw new Error(NOT_LOGGED_IN_ERROR_MESSAGE)
	}

	const ref = projectReference(user, projectId)
	const projectStarted = Date.now()

	let doc = await documentFromCacheOrUndefined(ref)
	const cached = doc !== undefined
	if (!doc) {
		doc = await getDoc(ref)
	}
	console.log(`DATABASE: read project ${projectId} from ${cached ? 'cache' : 'server'} in ${Date.now() - projectStarted}ms`)

	const project = doc.data() as dt.ProjectWithProducts
	if (!project) {
		return undefined
	}

	project.products = {}

	/* Load products. A cached project has had its products cached alongside it, so an empty result there
	   means the project really has no products, rather than us not having downloaded them yet.
	 */
	const productsRef = productsCollection(user, projectId)
	const productsStarted = Date.now()
	const productDocs = cached ? await getDocsFromCache(productsRef) : await getDocs(productsRef)
	console.log(`DATABASE: read ${productDocs.size} products in project ${projectId} from ${cached ? 'cache' : 'server'} in ${Date.now() - productsStarted}ms`)

	productDocs.docs.forEach((productDoc) => {
		project.products[productDoc.id] = productDoc.data()
	})
	return project
}

export async function projectById(projectId: dt.ProjectID): Promise<dt.ProjectWithProducts> {
	const result = await projectByIdOrUndefined(projectId)
	if (!result) {
		throw new Error(`Project ${projectId} was not found`)
	}
	if (result.deleted) {
		throw new Error(`Project ${projectId} is deleted`)
	}
	return result
}
